import type { ConversationsChannels } from 'lib-shared-legacy-types';

export type MessageSubmissionValidation =
  | { canSubmit: true; submissionType: 'text' | 'attachment-only' }
  | { canSubmit: false; errorType: 'required' | 'length' };

export type ValidateMessageSubmissionParams = {
  /** The message text content */
  text: string | undefined | null;
  /** The current channel */
  channel: ConversationsChannels;
  /** Whether the WhatsApp outbound attachments feature flag is enabled */
  hasWhatsAppAttachmentsFeature: boolean;
  /** The current attachments */
  attachments: File[] | undefined;
  /** Function to check if text exceeds max length */
  isMaxLengthExceeded: (text: string) => boolean;
};

/**
 * Validates whether a message can be submitted based on the channel and
 * content.
 *
 * Rules:
 *
 * - All channels: Text is required unless specific exceptions apply
 * - All channels: Text must not exceed max length
 * - WhatsApp (with feature flag): Can send attachment-only without text
 */
export function validateMessageSubmission({
  text,
  channel,
  hasWhatsAppAttachmentsFeature,
  attachments,
  isMaxLengthExceeded,
}: ValidateMessageSubmissionParams): MessageSubmissionValidation {
  const hasText = text && typeof text === 'string' && text.trim() !== '';
  const hasAttachments = Array.isArray(attachments) && attachments.length > 0;

  // Check if this is WhatsApp with attachments-only (no text required)
  const isWhatsAppAttachmentsOnly =
    channel === 'whatsapp' &&
    hasWhatsAppAttachmentsFeature &&
    !hasText &&
    hasAttachments;

  if (hasText) {
    // Check max length
    if (isMaxLengthExceeded(text)) {
      return { canSubmit: false, errorType: 'length' };
    }
    return { canSubmit: true, submissionType: 'text' };
  }

  if (isWhatsAppAttachmentsOnly) {
    // WhatsApp allows sending attachments without text
    return { canSubmit: true, submissionType: 'attachment-only' };
  }

  // No text and not a valid attachment-only scenario
  return { canSubmit: false, errorType: 'required' };
}
